import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Megaphone, Loader2, Send, Plus } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { currentLocale } from '@/lib/i18n'
import { CreateBroadcastCampaignDialog } from './create-broadcast-campaign-dialog'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  useChienDichNhanTin,
  tienDo,
  NHAN_TRANG_THAI,
  type ChienDichNhanTin,
  type TrangThaiChienDich,
} from '@/hooks/use-zalo-broadcast-campaigns'

const MAU: Record<TrangThaiChienDich, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  draft: 'outline',
  scheduled: 'outline',
  running: 'default',
  paused: 'secondary',
  completed: 'secondary',
  cancelled: 'destructive',
}

export function BroadcastCampaignsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('marketing')
  const { data: rows = [], isLoading } = useChienDichNhanTin()
  const [moTao, setMoTao] = useState(false)
  // Hộp tạo gọi POST tạo (broadcast_campaigns.create) rồi nạp người nhận (…/targets/import → broadcast_campaigns.update)
  // → thiếu một trong hai là ẩn nút, tránh để lại chiến dịch nháp rỗng (BRD §5.3.1).
  const { data: perms } = useMyPermissions()
  const coQuyenTao = !!perms?.has('broadcast_campaigns.create') && !!perms?.has('broadcast_campaigns.update')

  const tong = useMemo(() => ({
    chienDich: rows.length,
    dangChay: rows.filter((c) => c.status === 'running').length,
    daGui: rows.reduce((s, c) => s + c.sentCount, 0),
  }), [rows])

  const cot: Column<ChienDichNhanTin>[] = [
    {
      key: 'name',
      header: t('broadcastCampaigns.columns.name'),
      cell: (r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          <div className="text-xs text-muted-foreground">
            {t('broadcastCampaigns.accountsCreated', { count: r.accounts.length, date: dayjs(r.createdAt).format('DD/MM/YYYY') })}
            {r.status === 'scheduled' && r.scheduledAt && ` · ${t('broadcastCampaigns.scheduledAtInline', { date: dayjs(r.scheduledAt).format('HH:mm DD/MM') })}`}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('broadcastCampaigns.columns.status'),
      cell: (r) => <Badge variant={MAU[r.status]}>{NHAN_TRANG_THAI[r.status]}</Badge>,
    },
    {
      key: 'tien',
      header: t('broadcastCampaigns.columns.sentTotal'),
      align: 'right',
      cell: (r) => (
        <span className="tabular-nums">
          {r.sentCount.toLocaleString(currentLocale())} / {r.totalCount.toLocaleString(currentLocale())}
          {r.totalCount > 0 && (
            <span className="ml-2 text-xs text-muted-foreground">({(tienDo(r) * 100).toFixed(0)}%)</span>
          )}
        </span>
      ),
    },
    {
      key: 'loi',
      header: t('broadcastCampaigns.columns.failedSkipped'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.failedCount} / {r.skippedCount}</span>,
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('broadcastCampaigns.title')}
        description={t('broadcastCampaigns.description')}
        actions={
          coQuyenTao && (
            <Button onClick={() => setMoTao(true)}>
              <Plus className="h-4 w-4" /> {t('broadcastCampaigns.create')}
            </Button>
          )
        }
      />

      {coQuyenTao && <CreateBroadcastCampaignDialog open={moTao} onOpenChange={setMoTao} />}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label={t('broadcastCampaigns.stats.total')} value={tong.chienDich} icon={Megaphone} />
        <StatCard label={t('broadcastCampaigns.stats.running')} value={tong.dangChay} icon={Loader2} tone="warning" />
        <StatCard label={t('broadcastCampaigns.stats.sent')} value={tong.daGui} icon={Send} tone="primary" />
      </div>

      <DataTable
        columns={cot}
        rows={rows}
        loading={isLoading}
        rowKey={(r) => r.id}
        emptyTitle={t('broadcastCampaigns.empty')}
        onRowClick={(r) => navigate(`/zalo-broadcast-campaigns/${r.id}`)}
      />
    </div>
  )
}
